import { useState } from "react";
import { HelpCircle, Send, ChevronDown } from "lucide-react";

const InteractiveQA = ({ questions, onSubmitAnswers, patientName }) => {
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!questions || questions.length === 0) {
    return null; // Don't render if no questions
  }

  const handleAnswerChange = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmitAnswers(answers);
    } catch (error) {
      console.error("Error submitting answers:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).filter(
      (key) => answers[key] !== undefined && answers[key] !== ""
    ).length;
  };

  const renderQuestion = (question) => {
    const currentAnswer = answers[question.id];

    switch (question.type) {
      case "dropdown":
        return (
          <div className="relative">
            <select
              value={currentAnswer || ""}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white pr-10"
            >
              <option value="">Select an option...</option>
              {question.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        );

      case "text":
        return (
          <div>
            <textarea
              value={currentAnswer || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 200) {
                  handleAnswerChange(question.id, value);
                }
              }}
              placeholder="Enter your response..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              rows={3}
              maxLength={200}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {(currentAnswer || "").length}/200 characters
            </div>
          </div>
        );

      case "toggle":
        return (
          <div className="flex space-x-4">
            <button
              onClick={() => handleAnswerChange(question.id, "Yes")}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                currentAnswer === "Yes"
                  ? "border-green-500 bg-green-50 text-green-700"
                  : "border-gray-300 bg-white text-gray-700 hover:border-green-300"
              }`}
            >
              Yes
            </button>
            <button
              onClick={() => handleAnswerChange(question.id, "No")}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                currentAnswer === "No"
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-gray-300 bg-white text-gray-700 hover:border-red-300"
              }`}
            >
              No
            </button>
          </div>
        );

      default:
        return (
          <div className="text-gray-500 italic">
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <HelpCircle className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Follow-up Questions
          </h3>
          <p className="text-sm text-gray-600">
            Help refine the analysis for {patientName} by answering these
            questions
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {questions.map((question, index) => (
          <div key={question.id} className="bg-gray-50 rounded-lg p-4">
            <div className="mb-3">
              <div className="flex items-start justify-between">
                <h4 className="font-medium text-gray-900 flex-1 pr-4">
                  {index + 1}. {question.question}
                </h4>
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide flex-shrink-0">
                  {question.type}
                </span>
              </div>
              {question.rationale && (
                <p className="text-sm text-gray-600 mt-1 italic">
                  {question.rationale}
                </p>
              )}
            </div>

            {renderQuestion(question)}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {getAnsweredCount()} of {questions.length} questions answered
          <span className="text-gray-400 ml-2">
            (Optional - you can submit partial answers)
          </span>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          {isSubmitting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Send size={16} />
          )}
          <span>{isSubmitting ? "Analyzing..." : "Get Refined Analysis"}</span>
        </button>
      </div>
    </div>
  );
};

export default InteractiveQA;
